import { createFileRoute } from "@tanstack/react-router";
import THREEAssetLoader from "gfx/THREEAssetLoader";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import * as THREE from "three";
import withAuthentication from "../../components/withAuthentication";
import styles from "./styles/gfxDebugPage.module.scss";

function ExrTestPage() {
  const viewerContainerRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | undefined>(undefined);
  const sceneRef = useRef<THREE.Scene | undefined>(undefined);
  const cameraRef = useRef<THREE.Camera | undefined>(undefined);

  const [exposure, setExposure] = useState<number>(1.0);

  useEffect(() => {
    if (rendererRef.current) {
      return undefined;
    }

    const renderer = (rendererRef.current = new THREE.WebGLRenderer());
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    viewerContainerRef.current?.appendChild(renderer.domElement);

    renderer.toneMapping = THREE.ReinhardToneMapping;
    renderer.toneMappingExposure = exposure;

    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const scene = (sceneRef.current = new THREE.Scene());

    const aspect = window.innerWidth / window.innerHeight;
    const camera = (cameraRef.current = new THREE.OrthographicCamera(-aspect, aspect, 1, -1, 0, 1));

    void (async () => {
      const assetLoader = THREEAssetLoader.instance;
      // Image source: https://polyhaven.com/a/evening_road_01_puresky license: CC0
      const texture = await assetLoader.loadTexture("/static/debug/evening_road_01_puresky_1k.exr", true);
      const material = new THREE.MeshBasicMaterial({ map: texture });
      const quad = new THREE.PlaneGeometry(2.0, 1.0);
      const mesh = new THREE.Mesh(quad, material);
      scene.add(mesh);
      renderer.render(scene, camera);
    })();

    return () => {
      if (rendererRef.current) {
        if (rendererRef.current.domElement.parentElement) {
          rendererRef.current.domElement.parentElement.removeChild(rendererRef.current.domElement);
        }
        const renderer = rendererRef.current;
        renderer.dispose();
        rendererRef.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      const renderer = rendererRef.current;
      renderer.toneMappingExposure = exposure;
      renderer.render(sceneRef.current, cameraRef.current);
    }
  }, [exposure]);

  return (
    <>
      <Head>
        <title>{getPageTitle("EXR Loading Test")}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <div>
        <div className="page">
          <div className={styles.fullScreenViewerContainer} ref={viewerContainerRef} />
          <div className={styles.buttonContainerBottomLeft} style={{ background: "white", padding: "20px" }}>
            Exposure:{" "}
            <input
              type="range"
              value={exposure}
              min={0.1}
              max={2.0}
              step={0.1}
              onChange={(ev) => setExposure(parseFloat(ev.target.value))}
            ></input>
          </div>
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/exr-test")({
  component: withAuthentication(ExrTestPage, { superAdmin: true }),
});
